import type { ReactNode } from 'react';

import { MsalProvider } from '@azure/msal-react';

import { msalInstance } from '@frontend/features/auth/config/msalInstance';
import { getAuthMode } from '@frontend/features/auth/util/util.auth.getAuthMode';
import { isLocalhost } from '@frontend/features/auth/util/util.auth.isLocalhost';

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Wrapper component that conditionally uses MsalProvider based on environment and auth mode
 *
 * **Localhost** (any token state):
 * - Always bypasses MSAL (renders children directly)
 * - Requires E2E token exchange to authenticate
 * - Used for local development with token from deployed environment
 *
 * **E2E Mode** (token in localStorage on deployed environments):
 * - Bypasses MSAL (renders children directly)
 * - Works on dev, qa, prod with E2E token
 * - Used by Playwright E2E tests
 *
 * **MSAL Mode** (deployed environments, no E2E token):
 * - Wraps children with MsalProvider for Azure AD authentication
 * - Default mode on dev, qa, prod without E2E token
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const authMode = getAuthMode();

  // Force token exchange mode on localhost (never use MSAL)
  // Or use E2E mode when token is present (any environment)
  if (isLocalhost() || authMode === 'e2e') {
    return <>{children}</>;
  }

  // MSAL mode: Only on deployed environments without E2E token
  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
}
